/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { AgendaView } from '@features/agenda/utils/agenda-dates';

/** Resolves a localized (short) month name from its 0-based index. */
export type MonthNameFn = (monthIndex: number) => string;

/** Resolves a localized weekday name from its 0-based index (0=Sunday). */
export type WeekdayNameFn = (dayIndex: number) => string;

/** Resolves a localized full month name from its 0-based index. */
export type MonthLongFn = (monthIndex: number) => string;

/**
 * Builds the month-view header label (`Month Year`).
 * @param anchor - The anchor date.
 * @param monthShort - Short month-name resolver.
 * @param monthLong - Optional long month-name resolver.
 * @returns The localized month/year label.
 */
const labelMonth = (
  anchor: Date,
  monthShort: MonthNameFn,
  monthLong?: MonthLongFn
): string => {
  const mLong = monthLong ? monthLong(anchor.getMonth()) : monthShort(anchor.getMonth());
  return `${mLong} ${anchor.getFullYear()}`;
};

/**
 * Builds the 3-day-view header label spanning the anchor and next two days.
 * @param anchor - The anchor date.
 * @param monthShort - Short month-name resolver.
 * @returns The localized 3-day range label.
 */
const label3Day = (anchor: Date, monthShort: MonthNameFn): string => {
  const end = new Date(anchor);
  end.setDate(end.getDate() + 2);
  const startMonth = monthShort(anchor.getMonth());
  const endMonth = monthShort(end.getMonth());
  if (anchor.getMonth() === end.getMonth()) {
    return `${anchor.getDate()} a ${end.getDate()} ${endMonth}`;
  }
  return `${anchor.getDate()} ${startMonth} a ${end.getDate()} ${endMonth}`;
};

/**
 * Builds the default (week/day) header label for the anchor date.
 * @param anchor - The anchor date.
 * @param monthShort - Short month-name resolver.
 * @param weekday - Optional weekday-name resolver.
 * @param monthLong - Optional long month-name resolver.
 * @returns The localized default header label.
 */
const labelDefault = (
  anchor: Date,
  monthShort: MonthNameFn,
  weekday?: WeekdayNameFn,
  monthLong?: MonthLongFn
): string => {
  if (weekday && monthLong) {
    return `${weekday(anchor.getDay())}, ${anchor.getDate()} de ${monthLong(anchor.getMonth())}`;
  }
  return `${anchor.getDate()} ${monthShort(anchor.getMonth())} ${anchor.getFullYear()}`;
};

/**
 * Human-readable label for the toolbar. Shows the current anchor date in a
 * long format: "Weekday, DD de Month" (matching the prototype).
 *
 * For month view it shows just "Month YYYY".
 *
 * @param view The current agenda view.
 * @param anchor The anchor date for the view.
 * @param monthShort Resolver for localized short month names (used for month view).
 * @param weekday Optional resolver for localized weekday names.
 * @param monthLong Optional resolver for localized full month names.
 * @returns The localized period label.
 */
export const agendaLabel = (
  view: AgendaView,
  anchor: Date,
  monthShort: MonthNameFn,
  weekday?: WeekdayNameFn,
  monthLong?: MonthLongFn
): string => {
  const builders: Partial<Record<AgendaView, () => string>> = {
    month: () => labelMonth(anchor, monthShort, monthLong),
    '3day': () => label3Day(anchor, monthShort),
  };
  const builder = builders[view];
  return builder
    ? builder()
    : labelDefault(anchor, monthShort, weekday, monthLong);
};
